import { useQueryState, parseAsString } from "nuqs";
import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const usePartnerCode = () => {
  const { toast } = useToast();
  const [partnerCode] = useQueryState(
    "partnerCode",
    parseAsString.withOptions({ clearOnDefault: true }).withDefault("")
  );

  // Validate partner code
  const { data: codeInfo, isLoading } = useQuery({
    queryKey: ['partner-code', partnerCode],
    queryFn: async () => {
      if (!partnerCode) return null;
      
      const { data, error } = await supabase
        .from('partner_codes')
        .select('partner_name, credits_value, is_active, valid_until')
        .eq('code', partnerCode)
        .eq('is_active', true)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!partnerCode,
  });

  // Redeem code after signup
  const redeemMutation = useMutation({
    mutationFn: async ({ userId, code }: { userId: string; code: string }) => {
      if (!code) throw new Error('No partner code');
      
      const { data, error } = await supabase
        .rpc('redeem_partner_code', {
          p_code: code,
          p_user_id: userId,
          p_stripe_payment_intent: null
        });
      
      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; credits_granted?: number; partner_name?: string };
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to redeem code');
      }
      
      return result;
    },
    onSuccess: (data) => {
      toast({
        title: "Partner offer activated!",
        description: `You received ${data.credits_granted} credits from ${data.partner_name}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to apply partner code",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    partnerCode,
    codeInfo,
    isValidating: isLoading,
    redeemCode: (userId: string, code: string) => redeemMutation.mutateAsync({ userId, code }),
    isRedeeming: redeemMutation.isPending,
  };
};
